import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, ArrowLeft, Lock } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useUser } from '../../context/UserContext';
import api from '../../services/api';
import Button from '../common/Button';

const CartPage = () => {
    const { cart, cartTotal, removeFromCart, updateQuantity, clearCart } = useCart();
    const { user } = useUser();
    const navigate = useNavigate();

    if (cart.length === 0) {
        return (
            <div className="min-h-[70vh] flex flex-col items-center justify-center pt-32 pb-24">
                <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center text-green-600 mb-8 animate-bounce">
                    <ShoppingBag className="w-12 h-12" />
                </div>
                <h1 className="text-4xl font-extrabold text-green-950 mb-4">Votre panier est vide</h1>
                <p className="text-lg text-slate-600 mb-10 max-w-md text-center">
                    Découvrez nos collections exceptionnelles et trouvez la pièce qui transformera votre intérieur.
                </p>
                <Link to="/">
                    <Button variant="primary" className="h-14 px-10 text-lg">
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Reprendre mes achats
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-white pt-32 pb-24">
            <div className="container mx-auto px-6">
                <div className="flex flex-col lg:flex-row gap-16 items-start">
                    {/* Cart Items List */}
                    <div className="flex-grow space-y-8">
                        <div className="flex items-center justify-between pb-8 border-b border-green-100">
                            <h1 className="text-4xl font-extrabold text-green-950">Mon Panier</h1>
                            <button
                                onClick={clearCart}
                                className="text-sm font-bold text-red-500 hover:text-red-700 transition-colors uppercase tracking-widest"
                            >
                                Vider le panier
                            </button>
                        </div>

                        <div className="space-y-6">
                            {cart.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex flex-col sm:flex-row items-center gap-8 p-6 rounded-[2rem] bg-green-50/30 border border-green-100 group transition-all hover:shadow-xl hover:bg-white"
                                >
                                    <div className="w-32 h-32 rounded-2xl overflow-hidden bg-white shrink-0 shadow-sm transition-transform group-hover:scale-105">
                                        <img
                                            src={item.image.startsWith('http') ? item.image : `${api.defaults.baseURL}${item.image}`}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    <div className="flex-grow flex flex-col sm:flex-row items-center justify-between gap-6 w-full">
                                        <div>
                                            <p className="font-bold text-green-950 text-xl">{item.name}</p>
                                            <p className="text-lg font-bold text-green-600">{Math.floor(item.price).toLocaleString('fr-FR')} Fcfa</p>
                                        </div>

                                        <div className="flex items-center gap-6">
                                            <div className="flex items-center border-2 border-green-100 rounded-full h-12 bg-white px-2">
                                                <button
                                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                    className="w-8 h-8 flex items-center justify-center text-green-900 hover:text-green-500 disabled:opacity-30 transition-colors"
                                                >
                                                    <Minus className="w-4 h-4" />
                                                </button>
                                                <span className="w-8 text-center font-bold text-green-950">{item.quantity}</span>
                                                <button
                                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                    disabled={item.stock !== undefined && item.quantity >= item.stock}
                                                    className="w-8 h-8 flex items-center justify-center text-green-900 hover:text-green-500 disabled:opacity-30 transition-colors"
                                                    title={item.stock !== undefined && item.quantity >= item.stock ? "Stock maximum atteint" : "Ajouter"}
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                className="w-12 h-12 rounded-full flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Order Summary Sidebar */}
                    <div className="w-full lg:w-[400px] sticky top-32">
                        <div className="p-10 rounded-[2.5rem] bg-green-950 text-white shadow-2xl">
                            <h2 className="text-2xl font-bold mb-8">Résumé de la commande</h2>

                            <div className="space-y-6 mb-10 pb-10 border-b border-white/10">
                                <div className="flex justify-between items-center text-slate-600">
                                    <span className="font-bold">Sous-total</span>
                                    <span className="text-xl font-bold">{Math.floor(cartTotal).toLocaleString('fr-FR')} Fcfa</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-white/60 font-medium">Livraison</span>
                                    <span className="text-green-400 font-bold uppercase tracking-widest text-xs">Gratuite</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-end mt-4 pt-4 border-t border-green-100">
                                <div>
                                    <span className="block font-black text-green-950 text-lg">Total</span>
                                    <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">TTC</span>
                                </div>
                                <span className="text-3xl font-extrabold text-brand">{Math.floor(cartTotal).toLocaleString('fr-FR')} Fcfa</span>
                            </div>

                            {user ? (
                                <Link to="/checkout" className="block w-full">
                                    <Button variant="primary" className="w-full h-16 text-lg tracking-wide shadow-brand/20">
                                        Passer au paiement
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                            ) : (
                                <div className="space-y-4">
                                    <Button
                                        variant="primary"
                                        className="w-full h-16 text-lg tracking-wide opacity-50 cursor-not-allowed group"
                                        onClick={() => navigate('/auth')}
                                    >
                                        <Lock className="w-5 h-5 mr-2" />
                                        Connectez-vous pour payer
                                    </Button>
                                    <p className="text-center text-xs text-brand/60 font-medium">
                                        L'authentification est requise pour passer commande.
                                    </p>
                                </div>
                            )}

                            <div className="mt-8 pt-8 border-t border-white/10 text-center">
                                <p className="text-xs text-white/40 leading-relaxed">
                                    Paiement 100% sécurisé via Stripe ou PayPal. Livraison Premium assurée par nos soins.
                                </p>
                            </div>
                        </div>

                        <Link
                            to="/"
                            className="mt-8 flex items-center justify-center gap-2 text-green-600 font-bold hover:gap-3 transition-all"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            Continuer les achats
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartPage;
